import he from 'he';
import DOMPurify from 'dompurify';
import wooCommerceStore from '../stores/woocommerceStore';

function decodeHtmlEntity(entity) {
  return he.decode(entity);
}

export const sanitize = (content, stripHtml = false) => {
  if (!content) return '';

  if (stripHtml) {
    // Server-side or client-side HTML stripping
    if (typeof window === 'undefined') {
      // Server-side: Use regex to strip HTML tags
      return content.replace(/<[^>]*>/g, '').trim();
    } else {
      // Client-side: Use DOM API
      const temp = document.createElement('div');
      temp.innerHTML = content;
      return (temp.textContent || temp.innerText || '').trim();
    }
  }

  // Regular sanitization
  if (typeof window === 'undefined') {
    // Server-side: Basic HTML sanitization
    return content;
  }
  // Client-side: Use DOMPurify
  return DOMPurify.sanitize(content);
};

export const formatPrice = (price) => {
  const currencyCode = wooCommerceStore.currency;
  const currencyPosition = wooCommerceStore.currencyPos;
  const currencySymbol = wooCommerceStore.currencySymbol;
  const thousandSep = wooCommerceStore.thousandSep || ',';
  const decimalSep = wooCommerceStore.decimalSep || '.';
  const decimals = wooCommerceStore.numDecimals || 2;

  if (!currencySymbol || !currencyCode) {
      console.error('currencySymbol or currency code is not defined');
      return price;
  }

  if (!currencySymbol) {
      console.error('Currency symbol not found for the given currency code');
      return price;
  }

  // Convert HTML entity to actual symbol
  const symbol = decodeHtmlEntity(currencySymbol);

  // Format the price with the appropriate separators and decimal places
  const formattedPrice = parseFloat(price)
      .toFixed(decimals)
      .replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep)
      .replace('.', decimalSep);

  // Position the currency symbol according to the setting
  switch (currencyPosition) {
      case 'left':
          return `${symbol}${formattedPrice}`;
      case 'right':
          return `${formattedPrice}${symbol}`;
      case 'left_space':
          return `${symbol} ${formattedPrice}`;
      case 'right_space':
          return `${formattedPrice} ${symbol}`;
      default:
          return formattedPrice;
  }
};

export const getPriceDetails = (product, weightUnit, tu, productType = "simple") => {
  const { regular_price: regularPrice, sale_price: salePrice, weight, units_based: productUnitsOption, pack_based: productPackOption, weightable: productWeightOption, weight_quantity_based: productWeightQuantity } = product;
  let pricePerUnit = 0, salePricePerUnit = null, pricePer100g = 0, salePricePer100g = null, perUnit, displayWeight, perUnitDisplay = "", shortDescription = "";

  perUnit = tu(weightUnit);

  const isWeightValid = weight > 0;
  const isRegularPriceValid = regularPrice > 0;
  const isSalePriceValid = salePrice > 0;

  if (isWeightValid && isRegularPriceValid && productWeightOption) {
    switch (weightUnit) {
      case 'kg':
        pricePerUnit = regularPrice / weight;
        salePricePerUnit = isSalePriceValid ? salePrice / weight : null;
        pricePer100g = pricePerUnit / 10;
        salePricePer100g = salePricePerUnit ? salePricePerUnit / 10 : null;
        displayWeight = weight >= 1 ? `${weight} ${tu('kg')}` : `${weight * 1000} ${tu('g')}`;
        break;
      case 'g':
        pricePerUnit = regularPrice;
        salePricePerUnit = isSalePriceValid ? salePrice : null;
        pricePer100g = pricePerUnit / (weight / 100);
        salePricePer100g = salePricePerUnit ? salePricePerUnit / (weight / 100) : null;
        displayWeight = `${weight} ${tu('g')}`;
        break;
        case 'lbs':
          const weightInKg = weight * 2.20462; // Convert lbs to kg
          pricePerUnit = regularPrice / weightInKg;
          salePricePerUnit = salePrice ? salePrice / weightInKg : null;
          pricePer100g = pricePerUnit / 10;
          salePricePer100g = salePricePerUnit / 10;
          displayWeight = `${weightInKg.toFixed(2)} ${tu('lbs')}`;
          break;
        case 'oz':
          const weightInKgOz = weight * 35.274; // Convert oz to kg
          pricePerUnit = regularPrice / weightInKgOz;
          salePricePerUnit = salePrice ? salePrice / weightInKgOz : null;
          pricePer100g = pricePerUnit / 10;
          salePricePer100g = salePricePerUnit / 10;
          displayWeight = `${weightInKgOz.toFixed(2)} oz`;
          break;
      default:
        pricePerUnit = regularPrice / weight;
        salePricePerUnit = isSalePriceValid ? salePrice / weight : null;
        pricePer100g = pricePerUnit / 10;
        displayWeight = weight >= 1 ? `${weight} ${tu('kg')}` : `${weight * 1000} ${tu('g')}`;
    }
  } else {
    pricePerUnit = regularPrice;
    salePricePerUnit = salePrice ? salePrice : null;
  }

  if (productWeightOption) {
    perUnitDisplay = perUnit;
  }

  if ((productUnitsOption && !productWeightOption) || (productPackOption && !productWeightOption)) {
    pricePerUnit = regularPrice;
    salePricePerUnit = salePrice || null;
    perUnitDisplay = productUnitsOption ? tu('unit') : tu('pack');
  }

  if (productUnitsOption) {
    perUnit = tu('unit');
  }

  if (productPackOption) {
    perUnit = tu('pack');
  }

  if ((productType === "variable" && !productWeightOption) || (productType === "variation" && !productWeightOption)) {
    perUnit = product.name || (product.attributes && product.attributes.length > 0
        ? product.attributes.map(attr => attr.option).join(' / ')
        : "");
    pricePerUnit = regularPrice;
    salePricePerUnit = salePrice || null;
    perUnitDisplay = perUnit;
  }

  if ((productType === "variable" && productWeightOption && !productWeightQuantity) || (productType === "variation" && productWeightOption && !productWeightQuantity)) {
    const perUnitVar = product.name || (product.attributes && product.attributes.length > 0
        ? product.attributes.map(attr => attr.option).join(' / ')
        : "");
    perUnit = perUnitVar;
  }

  if (salePricePerUnit) {
    pricePer100g = salePricePer100g || null;
  }

  if (product?.short_description) {
    shortDescription = sanitize(product?.short_description, true);
  } else if (product?.description) {
    shortDescription = sanitize(product?.description, true);
  }

  return { pricePerUnit, salePricePerUnit, pricePer100g, displayWeight, perUnit, tu, perUnitDisplay, shortDescription };
};

export const renderPrice = ({ pricePerUnit, salePricePerUnit, shortDescription, variant = "desktop" }) => (
  <div className="text-secondary-text-studio">
    {salePricePerUnit ? (
      <>
        <div className="sale-price font-semibold text-secondary-studio">
          {formatPrice(salePricePerUnit)}
        </div>
        <div className="original-price text-sm">
          <span style={{ textDecoration: 'line-through' }}>{formatPrice(pricePerUnit)}</span>
        </div>
      </>
    ) : (
      <div className="font-semibold">
        {formatPrice(pricePerUnit)}
      </div>
    )}
    {variant === "desktop" && renderPriceInfo({ shortDescription })}
  </div>
);

export const renderVariationPrice = ({ pricePerUnit, salePricePerUnit, perUnitDisplay, shortDescription, variant = "desktop" }) => (
  <div className="text-secondary-text-studio">
    {salePricePerUnit ? (
      <>
        <div className="sale-price font-semibold text-secondary-studio">
          {formatPrice(salePricePerUnit)} / {perUnitDisplay}
        </div>
        <div className="original-price text-sm">
          <span style={{ textDecoration: 'line-through' }}>{formatPrice(pricePerUnit)} / {perUnitDisplay}</span>
        </div>
      </>
    ) : (
      <div className="font-semibold">
        {formatPrice(pricePerUnit)} / {perUnitDisplay}
      </div>
    )}
    {variant === "desktop" && renderPriceInfo({ shortDescription })}
  </div>
);

export const renderTypePrice = ({ pricePerUnit, salePricePerUnit, perUnitDisplay, shortDescription, variant = "desktop" }) => (
  <div className="text-secondary-text-studio">
    {salePricePerUnit ? (
      <>
        <div className="sale-price font-semibold text-secondary-studio">
          {formatPrice(salePricePerUnit)} / {perUnitDisplay}
        </div>
        <div className="original-price text-sm">
          <span style={{ textDecoration: 'line-through' }}>{formatPrice(pricePerUnit)}</span>
        </div>
      </>
    ) : (
      <div className="font-semibold">
        {formatPrice(pricePerUnit)} / {perUnitDisplay}
      </div>
    )}
    {variant === "desktop" && renderPriceInfo({ shortDescription })}
  </div>
);

export const renderWeightPrice = ({ pricePerUnit, salePricePerUnit, displayWeight, pricePer100g, perUnitDisplay, tu, shortDescription, variant = "desktop" }) => (
  <div className="text-secondary-text-studio">
    {salePricePerUnit ? (
      <>
        <div className="sale-price font-semibold text-secondary-studio">
          {formatPrice(salePricePerUnit)} / {perUnitDisplay}
        </div>
        <div className="original-price text-sm">
          <span style={{ textDecoration: 'line-through' }}>{formatPrice(pricePerUnit)}</span>
        </div>
      </>
    ) : (
      <div className="font-semibold">
        {formatPrice(pricePerUnit)} / {perUnitDisplay}
      </div>
    )}
    {variant === "desktop" && renderWeightPriceInfo({ displayWeight, pricePer100g, tu, shortDescription })}
  </div>
);

export const renderWeightPriceInfo = ({ displayWeight, pricePer100g, tu, shortDescription }) => (
  <>
    {shortDescription && <div className="text-xs">{shortDescription}</div>}
    {displayWeight && <div className="text-xs">{displayWeight}</div>}
    {pricePer100g && (
      <div className="text-xs">{formatPrice(pricePer100g)} {tu('pricePer100G')}</div>
    )}
  </>
);

export const renderPriceInfo = ({ shortDescription }) => (
  <>
    {shortDescription && <div className="text-xs">{shortDescription}</div>}
  </>
);

export const renderPriceCart = ({ pricePerUnit, salePricePerUnit }) => (
  <>
    {salePricePerUnit ? (
      <>
        <div className="sale-price text-secondary-studio">
          {formatPrice(salePricePerUnit)}
        </div>
        <div className="original-price">
          <span style={{ textDecoration: 'line-through' }}>{formatPrice(pricePerUnit)}</span>
        </div>
      </>
    ) : (
      <div>
        {formatPrice(pricePerUnit)}
      </div>
    )}
  </>
);

export const renderTypePriceCart = ({ pricePerUnit, salePricePerUnit, perUnitDisplay }) => (
  <>
    {salePricePerUnit ? (
      <>
        <div className="sale-price text-secondary-studio">
          {formatPrice(salePricePerUnit)} / {perUnitDisplay}
        </div>
        <div className="original-price">
          <span style={{ textDecoration: 'line-through' }}>{formatPrice(pricePerUnit)} / {perUnitDisplay}</span>
        </div>
      </>
    ) : (
      <div>
        {formatPrice(pricePerUnit)} / {perUnitDisplay}
      </div>
    )}
  </>
);

export const renderWeightPriceCart = ({ pricePerUnit, salePricePerUnit, perUnitDisplay }) => (
  <>
    {salePricePerUnit ? (
      <>
          <div className="sale-price text-secondary-studio">
          {formatPrice(salePricePerUnit)} / {perUnitDisplay}
        </div>
        <div className="original-price">
          <span style={{ textDecoration: 'line-through' }}>{formatPrice(pricePerUnit)} / {perUnitDisplay}</span>
        </div>
      </>
    ) : (
      <div>
        {formatPrice(pricePerUnit)} / {perUnitDisplay}
      </div>
    )}
  </>
);

export const renderVariationPriceCart = ({ pricePerUnit, salePricePerUnit, perUnitDisplay }) => (
  <>
    {salePricePerUnit ? (
      <>
        <div className="sale-price text-secondary-studio">
          {formatPrice(salePricePerUnit)} / {perUnitDisplay}
        </div>
        <div className="original-price">
          <span style={{ textDecoration: 'line-through' }}>{formatPrice(pricePerUnit)} / {perUnitDisplay}</span>
        </div>
      </>
    ) : (
      <div>
        {formatPrice(pricePerUnit)} / {perUnitDisplay}
      </div>
    )}
  </>
);

export const removeEmptyParams = (obj) => {
  Object.keys(obj).forEach(key => {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      removeEmptyParams(obj[key]); // Recursively call for nested objects
      // Remove the property if it becomes an empty object
      if (Object.keys(obj[key]).length === 0) {
        delete obj[key];
      }
    } else if (obj[key] === '') {
      delete obj[key]; // Remove the property if its value is an empty string
    }
  });
  return obj;
}

export const replaceBackendWithFrontendUrl = (data) => {
  const url = process.env.NEXT_PUBLIC_VERCEL_ENV === "production" ? process.env.NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL : process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL;

  const searchValue = process.env.NEXT_PUBLIC_WORDPRESS_SITE_URL.replace(/\/$/, ''); // Backend URL without trailing slash
  const replaceValue = `https://${url.replace(/\/$/, '')}`; // Ensure the frontend URL has the 'https://' protocol

  const replaceUrls = (str) => {
    // Avoid replacing if the frontend URL is already present
    if (str.includes(replaceValue)) return str;

    // Replace the backend URL with the frontend URL, except for the uploads directory
    let formattedData = str.replaceAll(searchValue, replaceValue);

    // Restore backend URL specifically for the uploads directory
    formattedData = formattedData.replaceAll(`${replaceValue}/wp-content/uploads`, `${searchValue}/wp-content/uploads`);

    // Remove trailing slashes from URLs that aren't file paths
    const urlPattern = /^(?:https?:\/\/[^\/]+|\/[^\/])[^\s]*$/i;
    if (urlPattern.test(formattedData) && // Check if it's a URL
        !formattedData.match(/\.[a-zA-Z0-9]{2,4}(\?.*)?$/) && // Check if it's not a file (with optional query params)
        formattedData.endsWith('/')) {
      formattedData = formattedData.slice(0, -1);
    }

    return formattedData;
  };

  if (typeof data === 'string') {
    return replaceUrls(data);
  } else if (typeof data === 'object' && data !== null) {
    const formattedData = Array.isArray(data) ? [] : {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        formattedData[key] = replaceBackendWithFrontendUrl(data[key]);
      }
    }
    return formattedData;
  } else {
    return data;
  }
};

export const isCustomPageUri = ( uri ) => {
        const pagesToExclude = [
                '/',
                '/login',
                '/register',
                '/my-account',
                '/checkout',
                '/cart',
                '/thank-you',
                '/reset-password',
        ];

        return pagesToExclude.includes( uri );
};

export const getPathNameFromUrl = ( url = '' ) => {
        if ( ! url ) {
                return '';
        }
        const theURL = new URL( url );
        return theURL.pathname;
}

export const handleRedirectsAndReturnData = ( defaultProps, data, field = '' ) => {
        // If no data is available then redirect to 503.
        if (!data) {
                return {
                        redirect: {
                                destination: '/503',
                                statusCode: 301,
                        },
                };
        }

        // If data for a given field is not available, redirect to 404.
        if (field && !data?.[ field ]) {
                return {
                        // returns the default 404 page with a status code of 404
                        notFound: true,
                };
        }

        return defaultProps;
};

export const hexToRgba = (hex, opacity, format = 'string') => {
  const bigint = parseInt(hex.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  if (format === 'array') {
    return [r, g, b, opacity];
  }

  if (format === 'object') {
    return { r, g, b, opacity };
  }

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export const adjustColor = (hex, percent) => {
  // Remove the hash symbol if it exists
  hex = hex.replace(/^#/, '');

  // Parse the red, green, and blue values
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Calculate the adjusted values
  r = Math.min(255, Math.max(0, r + Math.round(r * (percent / 100))));
  g = Math.min(255, Math.max(0, g + Math.round(g * (percent / 100))));
  b = Math.min(255, Math.max(0, b + Math.round(b * (percent / 100))));

  // Convert the values back to hex
  const newHex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;

  return newHex;
}

export const rgbaToHex = ({r, g, b, opacity}, format = 'hex') => {
  // Blend with white background (255, 255, 255)
  const blendedR = Math.round((1 - opacity) * 255 + opacity * r);
  const blendedG = Math.round((1 - opacity) * 255 + opacity * g);
  const blendedB = Math.round((1 - opacity) * 255 + opacity * b);

  if (format === 'rgb') {
    return `rgb(${blendedR}, ${blendedG}, ${blendedB})`;
  }

  // Convert to hex
  const toHex = (n) => {
    const hex = n.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(blendedR)}${toHex(blendedG)}${toHex(blendedB)}`;
};

// Also add a helper function to parse rgba string
export const parseRgba = (rgba) => {
  const matches = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([0-9.]+))?\)/);
  if (matches) {
    return {
      r: parseInt(matches[1], 10),
      g: parseInt(matches[2], 10),
      b: parseInt(matches[3], 10),
      a: matches[4] ? parseFloat(matches[4]) : 1,
    };
  }
  return null;
};
